import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, KeyRound, LogOut } from 'lucide-react';
import { cn } from '../../utils/cn';
import { User, UserRole } from '../../types/auth';

/**
 * Account menu in the app header.
 *
 * Replaces three flat header items - the user's name as plain text, a
 * "Change Password" button and a "Logout" button - with the single avatar +
 * dropdown every other enterprise app uses.
 *
 * Why the change: "Change Password" was a permanently visible, equally
 * weighted button sitting beside the page title on EVERY screen. It is a
 * rare, self-service account action, not a primary workflow, and giving it
 * top-level chrome both overstated it and crowded the header (the row also
 * carried the page title, description, chat and theme toggles). Account
 * actions belong behind the account identity, which is where users look for
 * them by convention.
 *
 * Accessibility follows the same contract as FilterSelect, the app's existing
 * popover: outside-click and Escape both dismiss (a popover that only closes
 * by re-clicking its trigger is a trap), the trigger declares
 * aria-haspopup/aria-expanded, and arrow keys move through the items. It uses
 * menu/menuitem roles rather than listbox/option because these are ACTIONS,
 * not a value being selected.
 */

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.ANALYST]: 'FinOps Analyst',
  [UserRole.VIEWER]: 'Viewer',
};

/** "Rajakumar Nadar" -> "RN"; falls back to the email's first letter. */
function initialsFor(user: User): string {
  const fromName = (user.name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  if (fromName) return fromName;
  return (user.email?.[0] ?? '?').toUpperCase();
}

export interface UserMenuProps {
  user: User;
  onChangePassword: () => void;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, onChangePassword, onLogout }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const roleLabel = user.roles?.length
    ? user.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')
    : undefined;

  const items = [
    {
      key: 'change-password',
      label: 'Change password',
      icon: KeyRound,
      onSelect: onChangePassword,
      danger: false,
    },
    {
      key: 'logout',
      label: 'Sign out',
      icon: LogOut,
      onSelect: onLogout,
      danger: true,
    },
  ];

  // Close on outside click / Escape - same contract as FilterSelect.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Opening with the keyboard should land focus inside the menu, otherwise a
  // keyboard user opens it and their next Tab leaves it entirely.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const onItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      itemRefs.current[(index + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      itemRefs.current[(index - 1 + items.length) % items.length]?.focus();
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.name || user.email}`}
        className="flex h-8 items-center gap-2 rounded-md px-1.5 text-sm text-foreground transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-2xs font-semibold uppercase text-primary"
          aria-hidden="true"
        >
          {initialsFor(user)}
        </span>
        {/* The name is decoration next to the avatar on small screens - the
            avatar plus aria-label already identify the control. */}
        <span className="hidden max-w-[12rem] truncate sm:inline">{user.name || user.email}</span>
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 opacity-50 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-md border bg-popover p-1 shadow-md"
        >
          {/* Identity block. Showing the email matters on a multi-tenant,
              invite-driven app where an admin may hold more than one account -
              the display name alone does not disambiguate which is signed in. */}
          <div className="border-b px-2 py-2">
            <p className="truncate text-sm font-medium text-foreground">{user.name || 'Signed in'}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            {roleLabel && (
              <span className="mt-1.5 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-medium text-primary">
                {roleLabel}
              </span>
            )}
          </div>

          <div className="pt-1">
            {items.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  onKeyDown={(e) => onItemKeyDown(e, index)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors focus:outline-none',
                    item.danger
                      ? 'text-rose-600 hover:bg-rose-500/10 focus:bg-rose-500/10 dark:text-rose-400'
                      : 'text-foreground hover:bg-secondary focus:bg-secondary'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
